import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { tituloTransferService, TransferTituloRequest } from '@/services/tituloTransferService';
import { kanbanService } from '@/services/kanbanService';
import { useAccessibleKanbans } from '@/hooks/useAccessibleKanbans';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { Textarea } from '@/components/ui/Textarea';
import { toast } from 'react-hot-toast';
import { Info } from 'lucide-react';

interface TituloTransferModalProps {
  isOpen: boolean;
  onClose: () => void;
  titulo: {
    id: string;
    numero: string;
    clienteNome: string;
    valor: number;
    kanbanId: string;
    kanbanNome: string;
    etapaId: string;
    etapaNome: string;
    /** Todos os títulos ativos do card a transferir (default: só `id`). */
    tituloIds?: string[];
  };
}


export const TituloTransferModal: React.FC<TituloTransferModalProps> = ({
  isOpen,
  onClose,
  titulo
}) => {
  console.log('🔍 TituloTransferModal renderizado com:', { isOpen, titulo });
  
  const queryClient = useQueryClient();
  const { data: accessibleKanbans = [] } = useAccessibleKanbans();
  const [selectedKanbanId, setSelectedKanbanId] = useState<string>('');
  const [selectedEtapaId, setSelectedEtapaId] = useState<string>('');
  const [motivo, setMotivo] = useState<string>('');
  const [availableEtapas, setAvailableEtapas] = useState<any[]>([]);

  const idsToTransfer = (titulo.tituloIds?.length ? titulo.tituloIds : [titulo.id]).filter(Boolean);

  // Filtrar kanbans disponíveis (excluir o kanban atual)
  const availableKanbans = accessibleKanbans.filter(kanban => 
    kanban.id !== titulo.kanbanId && kanban.isAtivo
  );

  // Buscar etapas do kanban selecionado
  const { data: etapas = [] } = useQuery({
    queryKey: ['kanban-etapas', selectedKanbanId],
    queryFn: () => kanbanService.getKanbanEtapas(selectedKanbanId),
    enabled: !!selectedKanbanId,
  });

  // Atualizar etapas disponíveis quando kanban muda
  useEffect(() => {
    if (selectedKanbanId) {
      setAvailableEtapas(etapas.filter(etapa => etapa.isAtivo));
      setSelectedEtapaId(''); // Reset etapa selection
    }
  }, [selectedKanbanId, etapas]);

  const transferMutation = useMutation({
    mutationFn: async (base: Omit<TransferTituloRequest, 'tituloId'>) => {
      const results = [];
      for (const tituloId of idsToTransfer) {
        results.push(
          await tituloTransferService.transferTitulo({
            ...base,
            tituloId,
          })
        );
      }
      return results;
    },
    onSuccess: (data) => {
      console.log('✅ transferMutation.onSuccess:', data);
      queryClient.invalidateQueries({ queryKey: ['titulos'] });
      queryClient.invalidateQueries({ queryKey: ['kanbans'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-simple'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-estatisticas'] });
      toast.success(
        idsToTransfer.length > 1
          ? `${idsToTransfer.length} títulos transferidos com sucesso!`
          : 'Título transferido com sucesso!'
      );
      onClose();
      resetForm();
    },
    onError: (error: any) => {
      console.error('❌ transferMutation.onError:', error);
      toast.error(error.response?.data?.message || 'Erro ao transferir título');
    },
  });

  const resetForm = () => {
    setSelectedKanbanId('');
    setSelectedEtapaId('');
    setMotivo('');
    setAvailableEtapas([]);
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  const handleTransfer = () => {
    console.log('🔍 handleTransfer chamado');
    console.log('📋 selectedKanbanId:', selectedKanbanId);
    console.log('📊 titulo:', titulo);
    
    if (!selectedKanbanId) {
      toast.error('Selecione um kanban de destino');
      return;
    }

    if (idsToTransfer.length === 0) {
      toast.error('Nenhum título para transferir');
      return;
    }

    transferMutation.mutate({
      fromKanbanId: titulo.kanbanId,
      toKanbanId: selectedKanbanId,
      toEtapaId: selectedEtapaId || undefined,
      motivo: motivo || undefined,
    });
  };

  const selectedKanban = availableKanbans.find(k => k.id === selectedKanbanId);
  const selectedEtapa = availableEtapas.find(e => e.id === selectedEtapaId);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Transferir Título" size="lg">
      <div className="space-y-6">
        {/* Informações do Título */}
        <div className="bg-muted/50 p-4 rounded-lg">
          <h4 className="font-medium mb-3">Título a ser transferido:</h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-muted-foreground">Número</label>
              <p className="font-medium">{titulo.numero}</p>
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Cliente</label>
              <p className="font-medium">{titulo.clienteNome}</p>
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Valor</label>
              <p className="font-medium">
                {new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL'
                }).format(titulo.valor)}
              </p>
            </div>
            <div>
              <label className="text-sm text-muted-foreground">Kanban Atual</label>
              <div className="flex items-center space-x-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: '#3b82f6' }}
                />
                <span className="font-medium">{titulo.kanbanNome}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Seleção do Kanban de Destino */}
        <div className="space-y-3">
          <label className="text-sm font-medium">Kanban de Destino *</label>
          <Select 
            value={selectedKanbanId} 
            onValueChange={setSelectedKanbanId}
            placeholder="Selecione o kanban de destino"
          >
            {availableKanbans.map((kanban) => (
              <option key={kanban.id} value={kanban.id}>
                {kanban.nome} {kanban.isDefault ? '(Padrão)' : ''}
              </option>
            ))}
          </Select>
        </div>

        {/* Seleção da Etapa (Opcional) */}
        {selectedKanbanId && availableEtapas.length > 0 && (
          <div className="space-y-3">
            <label className="text-sm font-medium">Etapa de Destino (Opcional)</label>
            <Select 
              value={selectedEtapaId} 
              onValueChange={setSelectedEtapaId}
              placeholder="Selecione uma etapa específica"
            >
              <option value="">
                Primeira etapa disponível
              </option>
              {availableEtapas.map((etapa) => (
                <option key={etapa.id} value={etapa.id}>
                  {etapa.nome} (Ordem {etapa.ordem})
                </option>
              ))}
            </Select>
          </div>
        )}

        {/* Motivo da Transferência */}
        <div className="space-y-3">
          <label className="text-sm font-medium">Motivo da Transferência (Opcional)</label>
          <Textarea
            placeholder="Descreva o motivo da transferência..."
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
          />
        </div>

        {/* Resumo da Transferência */}
        {selectedKanbanId && (
          <div className="bg-blue-50 dark:bg-blue-950/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-start space-x-2">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="space-y-2">
                <h4 className="font-medium text-blue-900 dark:text-blue-100">
                  Resumo da Transferência:
                </h4>
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <p>
                    <strong>De:</strong> {titulo.kanbanNome} → {titulo.etapaNome}
                  </p>
                  <p>
                    <strong>Para:</strong> {selectedKanban?.nome}
                    {selectedEtapa && ` → ${selectedEtapa.nome}`}
                    {!selectedEtapa && ' → Primeira etapa disponível'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Botões de ação */}
        <div className="flex justify-end space-x-2 pt-4 border-t">
          <Button variant="outline" onClick={handleClose}>
            Cancelar
          </Button>
          <Button 
            onClick={handleTransfer}
            disabled={!selectedKanbanId || transferMutation.isPending}
            className="min-w-[120px]"
          >
            {transferMutation.isPending ? 'Transferindo...' : 'Transferir'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};